import { getAccountKeyForMailboxInfo, type MailboxInfo } from 'owa-client-types';
import type { EntityRewriteDiagnostics } from 'owa-search-query-parser/lib/getEntityRewriteDiagnostics';

interface DiagnosticsSnapshot {
    searchQueryId: string | undefined;
    diagnostics: EntityRewriteDiagnostics;
}

/**
 * The entity-recognition diagnostics computed when each account's most recent mail
 * search executed. Keyed by account and tagged with the executed search's query id so
 * feedback from one account (or for a different search) can never attach another
 * search's query text or person identities.
 *
 * Held outside the observable search store on purpose: it carries query text and
 * person identities (PII) that are only read on demand by user-consented feedback,
 * so it does not need reactivity and must not be serialized with store state.
 */
const snapshots = new Map<string, DiagnosticsSnapshot>();

/**
 * Records the diagnostics for the executed search. Called on every executed mail
 * search (with `undefined` when recognition was not attempted) so a prior search
 * cannot leave a stale snapshot behind for the account.
 */
export function setEntityRecognitionDiagnosticsSnapshot(
    mailboxInfo: MailboxInfo,
    searchQueryId: string | undefined,
    diagnostics: EntityRewriteDiagnostics | undefined
): void {
    const accountKey = getAccountKeyForMailboxInfo(mailboxInfo);
    if (diagnostics) {
        snapshots.set(accountKey, { searchQueryId, diagnostics });
    } else {
        snapshots.delete(accountKey);
    }
}

/**
 * Returns the snapshot for the account only when it belongs to the given search.
 */
export function getEntityRecognitionDiagnosticsSnapshot(
    mailboxInfo: MailboxInfo,
    searchQueryId: string | undefined
): EntityRewriteDiagnostics | undefined {
    const snapshot = snapshots.get(getAccountKeyForMailboxInfo(mailboxInfo));
    return snapshot && snapshot.searchQueryId === searchQueryId ? snapshot.diagnostics : undefined;
}

export function clearEntityRecognitionDiagnosticsSnapshotsForTest(): void {
    snapshots.clear();
}
